/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import Alert from 'components/ui/Alert';
import LoadingCircle from 'components/ui/LoadingCircle';
import _ from 'lodash';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';
import CargomaticSelect from 'components/v2/select';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import { showToast } from 'stores/ToastStore';
import { findManyUsers } from 'utils/UserWebAPIUtils';
import { titleize } from 'underscore.string';
import { validateDocumentFilesSizes } from 'utils/documents';
import { BLANK_ACCESSORIAL_RATE, BLANK_CUSTOMER, DEFAULT_SEARCH_CUSTOMER_TYPE, STATUS, VALID_CUSTOMER_TYPE, } from 'constants/AccessorialRates';
import AddButton from '../../v2/buttons/AddButton';
import AddAccessorialCustomersMarketModal from '../carrier/modals/AddAccessorialLaneModal';
import AddAccessorialRatesModal from '../carrier/modals/AddAccessorialsModal';
import AuditTrailModal from './modals/LaneRateAuditTrailModal';
import AccessorialRatesList from './AccessorialRatesList';
import { accessorialRatesFilesMapper, customerDataMapper } from './helpers';
import { fetchExistingAccessorialRates } from '../carrier/modals/helpers/accessorial-rate-helpers';
const PER_PAGE = 20;
const ACTION_DATA = [
    { name: 'Update', slug: 'update', localizationSlug: 'UPDATE' },
];
export default function AccessorialRatesTable(props) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [actionType, setActionType] = useState('');
    const [accessorialRates, setAccessorialRates] = useState([
        BLANK_ACCESSORIAL_RATE,
    ]);
    const [missingRequiredFields, setMissingRequiredFields] = useState(false);
    const [disableFooterButtons, setDisableFooterButtons] = useState(true);
    const [filesUploaded, setFilesUploaded] = useState();
    const [showActionField, setShowActionField] = useState(false);
    const [rateAudit, setRateAudit] = useState();
    const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
    const [showAddCustomersMarketModal, setShowAddCustomersMarketModal] = useState(false);
    const [showAddAccessorialRatesModal, setShowAddAccessorialRatesModal] = useState(false);
    const [showEditAccessorialsModal, setShowEditAccessorialsModal] = useState(false);
    const [items, setItems] = useState([]);
    const [marketsList, setMarketsList] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [loadingConfirmation, setLoadingConfirmation] = useState(false);
    const [searchCustomerType, setSearchCustomerType] = useState(DEFAULT_SEARCH_CUSTOMER_TYPE);
    const [market, setMarket] = useState('');
    const [customerData, setCustomerData] = useState([]);
    const [isCustomerTypeValid, setIsCustomerTypeValid] = useState(true);
    const [areCustomersTheSameType, setAreCustomersTheSameType] = useState(true);
    const [areCustomersNaiveToMarket, setAreCustomersNaiveToMarket] = useState(true);
    const [duplicateCustomers, setDuplicateCustomers] = useState(false);
    const [customersWithMarketConfirmedRates, setCustomersWithMarketConfirmedRates,] = useState(new Set([]));
    const [isFetchingExistingCustomers, setIsFetchingExistingCustomers] = useState(false);
    function setFilterQueryParameters() {
        const { customer, status, market, customerType, autoApply } = props;
        const filterString = {};
        if (customer) {
            filterString.customer = customer;
        }
        if (market) {
            filterString.market = market;
        }
        if (status) {
            filterString.status = status;
        }
        if (customerType) {
            filterString.customerType = customerType;
        }
        if (typeof (autoApply) === 'boolean') {
            filterString.autoApply = autoApply;
        }
        return filterString;
    }
    async function getAccessorialRates() {
        const filterString = setFilterQueryParameters();
        const queryParams = new URLSearchParams(Object.assign({ per_page: PER_PAGE, skip }, filterString));
        if (sortColumn && sortDirection) {
            const sortAttributes = {
                sortColumn,
                sortDirection,
            };
            queryParams.append(sortAttributes);
        }
        const response = await WebAPIUtils.getLaneAccessorialRatesV2(queryParams);
        if (response) {
            const { laneAccessorialRates, count } = response;
            setItems(laneAccessorialRates);
            setPages(Math.ceil(count / PER_PAGE));
            setTotal(count);
        }
        setLoading(false);
    }
    function toggleSort(sortDirection, title) {
        setSortDirection(sortDirection);
        setSortColumn(title);
    }
    function handlePageClick(selectedPage) {
        setLoading(true);
        setPage(selectedPage);
        setSkip(((selectedPage || page) - 1) * PER_PAGE);
    }
    useEffect(() => {
        const getMarketsList = async () => {
            try {
                const marketsList = await MarketsAPIUtils.getMarkets();
                if (marketsList.entity.markets) {
                    const marketNames = _.map(marketsList.entity.markets, (market) => {
                        return {
                            _id: market.id,
                            name: market.name,
                            slug: market.name,
                        };
                    });
                    setMarketsList(marketNames);
                }
            }
            catch (e) {
                console.error('failed to fetch market values: ', e);
            }
        };
        getMarketsList();
        handleCustomerMarketValidation();
    }, []);
    useEffect(() => {
        setLoading(true);
        getAccessorialRates();
    }, [
        page,
        skip,
        sortColumn,
        sortDirection,
        props.customer,
        props.customerType,
        props.status,
        props.autoApply
    ]);
    useEffect(() => {
        const checked = items.filter((item) => item.checked === true);
        const showActionField = !!checked.length;
        setShowActionField(showActionField);
    }, [items]);
    useEffect(() => {
        handleCustomerMarketValidation();
    }, [customerData]);
    useEffect(() => {
        const fetchExistingCustomers = async () => {
            if (searchCustomerType !== 'customer' && market) {
                setIsFetchingExistingCustomers(true);
                const customerType = searchCustomerType.slice(0, -1).toLowerCase();
                try {
                    const laneAccessorialRates = await fetchExistingAccessorialRates({
                        status: STATUS.CONFIRMED,
                        market,
                        customerType,
                        paginate: false,
                    });
                    const uniqueCustomers = new Set(laneAccessorialRates.map((accessorialRate) => accessorialRate.customer));
                    setCustomersWithMarketConfirmedRates(uniqueCustomers);
                }
                catch (e) {
                    showToast(t('REQUEST_ERROR'), t('LOADING_ACCESSORIALS_ERROR'), 'error');
                }
                finally {
                    setIsFetchingExistingCustomers(false);
                }
            }
        };
        fetchExistingCustomers();
    }, [market, searchCustomerType]);
    function handleAuditClick(item) {
        setRateAudit(item);
        setShowAuditTrailModal(true);
    }
    function onActionTypeSelect(selected) {
        setActionType(selected.name);
    }
    const resetErrors = () => {
        setAreCustomersNaiveToMarket(true);
        setAreCustomersTheSameType(true);
        setIsCustomerTypeValid(true);
        setDuplicateCustomers(false);
    };
    const handleAddCustomer = () => {
        const currentCustomerData = [...customerData, BLANK_CUSTOMER];
        setCustomerData(currentCustomerData);
    };
    const validateCustomerType = (customerType) => {
        if (customerType && !VALID_CUSTOMER_TYPE.hasOwnProperty(customerType)) {
            setIsCustomerTypeValid(false);
            return false;
        }
        setIsCustomerTypeValid(true);
        return true;
    };
    const handleCustomerFieldChange = (index) => (type, item, value) => {
        const currentCustomerData = [...customerData];
        const newCustomerData = {};
        if (value) {
            const customerType = value.type.toLowerCase();
            const isValidCustomerType = validateCustomerType(customerType);
            if (!isValidCustomerType) {
                return;
            }
            newCustomerData.customer = value.id ? value.id : value._id;
            newCustomerData.customerType = customerType;
            newCustomerData.name = value.account_name
                ? value.account_name
                : value.name;
            const newSearchCustomerType = newCustomerData.customerType
                ? `${newCustomerData.customerType.toLocaleLowerCase()}s`
                : 'customer';
            if (searchCustomerType !== newSearchCustomerType) {
                setSearchCustomerType(newSearchCustomerType);
            }
        }
        currentCustomerData[index] = newCustomerData;
        setCustomerData(currentCustomerData);
    };
    const handleRemoveCustomerRow = (index) => () => {
        const newCustomerRows = [
            ...customerData.slice(0, index),
            ...customerData.slice(index + 1),
        ];
        setCustomerData(newCustomerRows);
        if (!newCustomerRows.length) {
            setSearchCustomerType(DEFAULT_SEARCH_CUSTOMER_TYPE);
            resetErrors();
        }
    };
    const handleAccessorialRateEdit = (accessorialRate) => {
        const { name, customer, customerType, market } = accessorialRate;
        setCustomerData([{ name, customer, customerType }]);
        setMarket(market);
        setShowEditAccessorialsModal(true);
    };
    const handleMarketChange = (market) => {
        setMarket(market.slug);
    };
    const handleCustomerMarketValidation = () => {
        let areRequiredFieldsMissing = !market;
        let dupeCustomers = false;
        let naiveCustomers = true;
        let sameType = true;
        areRequiredFieldsMissing = !customerData.length;
        const currCustomerData = [...customerData];
        let currCustomer;
        let areAllDataValid = !areRequiredFieldsMissing;
        const customerIds = {};
        const currSearchCustomerType = searchCustomerType.slice(0, -1);
        while (areAllDataValid && currCustomerData.length) {
            currCustomer = currCustomerData.pop();
            const currCustomerId = currCustomer.customer;
            if (customerIds.hasOwnProperty(currCustomerId)) {
                setDuplicateCustomers(true);
                dupeCustomers = true;
            }
            else {
                customerIds[currCustomerId] = currCustomerId;
                setDuplicateCustomers(false);
            }
            areRequiredFieldsMissing = !currCustomer.customer;
            if (customerData.length <= 1) {
                setAreCustomersNaiveToMarket(true);
                setDuplicateCustomers(false);
            }
            if (customerData.length > 1) {
                if (customersWithMarketConfirmedRates.has(currCustomer.customer)) {
                    setAreCustomersNaiveToMarket(false);
                    naiveCustomers = false;
                }
                else {
                    setAreCustomersNaiveToMarket(true);
                }
                if (currCustomer.customerType &&
                    currCustomer.customerType !== currSearchCustomerType) {
                    areRequiredFieldsMissing = false;
                    sameType = false;
                    setAreCustomersTheSameType(false);
                }
            }
            areAllDataValid = Boolean(naiveCustomers &&
                sameType &&
                !areRequiredFieldsMissing &&
                !dupeCustomers);
        }
        setMissingRequiredFields(areRequiredFieldsMissing);
    };
    useEffect(() => {
        const areAllDataValid = Boolean(areCustomersNaiveToMarket &&
            areCustomersTheSameType &&
            !missingRequiredFields &&
            !duplicateCustomers);
        setDisableFooterButtons(!areAllDataValid);
    }, [
        missingRequiredFields,
        areCustomersTheSameType,
        areCustomersNaiveToMarket,
        duplicateCustomers,
    ]);
    const onZoneBlur = async (zip) => {
        return LocationWebAPIUtils.zipToCityState(zip);
    };
    const handleCloseModal = (clear) => {
        if (clear) {
            setAccessorialRates([BLANK_CUSTOMER]);
            setCustomerData([BLANK_CUSTOMER]);
            setMarket('');
            resetErrors();
        }
        setShowAddCustomersMarketModal(false);
        setShowAddAccessorialRatesModal(false);
        setShowEditAccessorialsModal(false);
    };
    async function fetchCustomerEmail(availableAccessorialRates) {
        var _a;
        let customers = [];
        const uniqueCustomerIds = [
            ...new Set(availableAccessorialRates.map((accessorialRate) => accessorialRate.customer)),
        ];
        const userData = await findManyUsers(uniqueCustomerIds);
        const userDataItems = (_a = userData === null || userData === void 0 ? void 0 : userData.entity) === null || _a === void 0 ? void 0 : _a.items;
        if (userDataItems.length) {
            customers = userDataItems.reduce((accumulatedMappedData, currentValue) => accumulatedMappedData.concat(customerDataMapper(currentValue, availableAccessorialRates)), []);
        }
        else {
            customers = availableAccessorialRates;
        }
        return customers;
    }
    const handleComplete = (files) => {
        setFilesUploaded(files);
    };
    async function handleSave(laneAccessorialRates) {
        setLoadingConfirmation(true);
        try {
            setMissingRequiredFields(false);
            const mappedLaneAccessorialRateData = await fetchCustomerEmail(laneAccessorialRates);
            const postUpdateLaneRates = await WebAPIUtils.updateLaneAccessorialRates(mappedLaneAccessorialRateData);
            if (postUpdateLaneRates === null || postUpdateLaneRates === void 0 ? void 0 : postUpdateLaneRates.success) {
                showToast(t('REQUEST_SUCCESS'), t('ACCESSORIAL_LANE_RATE_SUCCESSFULLY_SUBMITTED'), 'success');
                await getAccessorialRates();
            }
            else {
                showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
            }
            setLoadingConfirmation(false);
            setShowAddCustomersMarketModal(false);
            setShowAddAccessorialRatesModal(false);
        }
        catch (e) {
            setLoadingConfirmation(false);
            showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
            console.error(e);
        }
    }
    const handleDropFiles = (index) => (files) => {
        setUploadingFiles(true);
        try {
            validateDocumentFilesSizes(files);
            setCustomerData((prev) => {
                const newCustomerData = [...prev];
                newCustomerData[index].files = files;
                return newCustomerData;
            });
        }
        catch (e) {
            // validateDocumentFileSizes displays a toast error when there's an error so no need to explicitly do anything here
        }
        setUploadingFiles(false);
    };
    async function handleSubmitAlreadyConfirmed(payloads) {
        await Promise.all(payloads.map(async (payload) => {
            try {
                await WebAPIUtils.alreadyConfirmedAccessorialRates(payload.accessorialRates, payload.files);
            }
            catch (e) {
                showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
                throw new Error(t('LANE_RATE_UPDATE_ERROR'));
            }
        }));
        return { success: true };
    }
    async function handleSubmitAccessorialRates(laneAccessorialRates) {
        setUploadingFiles(true);
        setLoadingConfirmation(true);
        try {
            const customers = await fetchCustomerEmail(laneAccessorialRates);
            const mapAccessorialRatesFiles = accessorialRatesFilesMapper(customers, customerData);
            const payloadsArray = Object.values(mapAccessorialRatesFiles);
            await handleSubmitAlreadyConfirmed(payloadsArray);
            showToast(t('REQUEST_SUCCESS'), t('ACCESSORIAL_LANE_RATE_SUCCESSFULLY_SUBMITTED'), 'success');
            await getAccessorialRates();
            handleCloseModal(true);
            await getAccessorialRates();
        }
        catch (e) {
            showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
            console.error(e);
        }
        finally {
            setLoadingConfirmation(false);
            setUploadingFiles(false);
        }
    }
    if (!SessionStore.isAdmin()) {
        return (React.createElement("div", { "data-testid": "div-aa03b28e", className: "p-4" },
            React.createElement(Alert, { dataTestId: "Alert-8cfef03d", title: t('NO_PERMISSION_TO_VIEW_PAGE'), type: "error" })));
    }
    const loadingText = t('LOADING');
    return (React.createElement("div", { "data-testid": "div-38d0a480", className: "bg-white rounded-md shadow divide-y divide-solid divide-gray-200 relative" },
        props.canUpdate && showActionField && (React.createElement("div", { "data-testid": "div-10c19b6c", className: "absolute w-1/10 right-16 top-3" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-0b28a880", hideLabel: true, items: ACTION_DATA, onSelect: onActionTypeSelect, defaultSelectLabel: "", value: actionType, className: "mb-4", valueKey: "slug", textKey: "localizationSlug", caption: titleize(t('ACTION')) }))),
        props.canUpdate && !loading && (React.createElement(AddButton, { dataTestId: "AddButton-98de2824", className: "absolute right-3 top-3", disabled: !props.canUpdate, onClick: () => setShowAddCustomersMarketModal(true) })),
        !loading && (React.createElement(AccessorialRatesList, { items: items, onPageClick: handlePageClick, perPage: PER_PAGE, page: page, pages: pages, sortDirection: sortDirection, sortColumn: sortColumn, total: total, toggleSort: toggleSort, canUpdate: props.canUpdate, onEdit: handleAccessorialRateEdit, setRateAudit: handleAuditClick })),
        React.createElement(AuditTrailModal, { show: showAuditTrailModal, rate: rateAudit, onHide: () => setShowAuditTrailModal(false), type: "laneAccessorialRates" }),
        showAddCustomersMarketModal && (React.createElement(AddAccessorialCustomersMarketModal, { showModal: showAddCustomersMarketModal, showAddAccessorialModal: showAddCustomersMarketModal, setShowAddAccessorialsModal: setShowAddAccessorialRatesModal, marketsArray: marketsList, customerData: customerData, searchCustomerType: searchCustomerType, onAddCustomer: handleAddCustomer, onChangeCustomer: handleCustomerFieldChange, onRemoveCustomerRow: handleRemoveCustomerRow, onClose: handleCloseModal, onMarketChange: handleMarketChange, market: market, onZoneBlur: onZoneBlur, missingRequiredFields: missingRequiredFields, disableButtons: disableFooterButtons, isCustomerTypeValid: isCustomerTypeValid, areCustomersNaiveToMarket: areCustomersNaiveToMarket, areCustomersTheSameType: areCustomersTheSameType, isFetchingExistingCustomers: isFetchingExistingCustomers, duplicateCustomers: duplicateCustomers })),
        showAddAccessorialRatesModal && (React.createElement(AddAccessorialRatesModal, { showModal: showAddAccessorialRatesModal, onClose: handleCloseModal, customerData: customerData, market: market, handleDropFiles: handleDropFiles, handleSubmit: handleSubmitAccessorialRates, onComplete: handleComplete, handleSave: handleSave, loadingConfirmation: loadingConfirmation, setLoadingConfirmation: setLoadingConfirmation, uploadingFiles: uploadingFiles, disableButtons: disableFooterButtons, onBack: () => {
                setShowAddAccessorialRatesModal(false);
                setShowAddCustomersMarketModal(true);
            } })),
        showEditAccessorialsModal && (React.createElement(AddAccessorialRatesModal, { showModal: showEditAccessorialsModal, handleDropFiles: handleDropFiles, handleSubmit: handleSubmitAccessorialRates, customerData: customerData, market: market, onComplete: handleComplete, handleSave: handleSave, setAccessorialRates: setAccessorialRates, loadingConfirmation: loadingConfirmation, setLoadingConfirmation: setLoadingConfirmation, onClose: handleCloseModal, uploadingFiles: uploadingFiles, disableButtons: disableFooterButtons, onBack: () => {
                setShowEditAccessorialsModal(false);
                setMarket('');
                setCustomerData([BLANK_CUSTOMER]);
            } })),
        loading && (React.createElement("div", { "data-testid": "div-813103fa", className: "p-4" },
            React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-085f72e6", text: loadingText })))));
}
